import { useEffect, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import { Table } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { Button, MiniBanner } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import { usePrevious } from "app/components/hooks/usePrevious";
import { translate } from "app/filters/i18nFilter";
import { WidgetBoxWithMarginBottom } from "app/pages/digital-marketing/ad-intelligence/common/StyledComponents";
import { ReportLoadingBanner } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/banners/ReportLoadingBanner";
import { useSidePanelWrapper } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/hooks/useSidePanelWrapper";
import { ALL_TOPICS_NAME } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import { NEW_PROMPT_ID_SUFFIX } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/consts";
import { usePeriodChangesData } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/hooks/usePeriodChangesData";
import { usePrompts } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/hooks/usePrompts";
import { getColumnConfig } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/config";
import {
    PAGE_SIZE,
    FETCH_PAGE_SIZE,
    EXCEL_API_ENDPOINT,
    DEFAULT_SORT_COLUMN,
    PROMPTS_TABLE_RETURN_LAST_DATE,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/consts";
import { useFiltersData } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/hooks/useFiltersData";
import { usePromptsUrlParams } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/hooks/usePromptsUrlParams";
import { PromptsDataFilters } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/PromptsDataFilters";
import { PromptsQuickFilters } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/PromptsQuickFilters";
import type {
    PromptsRequestParams,
    Prompt,
    IPromptsApiFilterParams,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/types";
import { extractDomain } from "app/pages/digital-marketing/utils/url";
import {
    useCampaignDetails,
    useUpdateCampaign,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/llm-tracker-service/useLLMTrackerCampaigns";
import {
    areAllModelsCompleted,
    getBaseWidgetRequestParams,
    getTopicsFilterParam,
    getWidgetNoDataProps,
    navigateToReport,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type {
    ILLMReportWidget,
    ILLMWidgetRequestParams,
    SortDirection,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { EAvailableTabs, ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { useColumnPickerTracking } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/useColumnPickerTracking";
import { TableWrapper } from "app/pages/digital-marketing/generative-ai/shared/styledComponents";
import { useAssetPermissions } from "app/services/asset-permissions/AssetPermissions";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { PromptsCounter } from "../../../shared/components/PromptsCounter";
import type { IModifiableTopic } from "../edit-modal/types";
import { usePromptsEditModal } from "../edit-modal/usePromptsEditModal";

import { PromptReportSidePanel } from "./PromptReportSidePanel";

const fetchService = DefaultFetchService.getInstance();

const SORT_FIELD_MAP: Partial<Record<string, string>> = {
    popChange: "popchange",
    visibility: "visibility",
};

const FullWidthMiniBanner = styled(MiniBanner)`
    width: 100%;
`;

const buildExcelUrl = (baseApiParams: ILLMWidgetRequestParams, filterParams: IPromptsApiFilterParams): string => {
    const excelParams = { ...baseApiParams, ...filterParams, returnLastDate: PROMPTS_TABLE_RETURN_LAST_DATE };
    return `${EXCEL_API_ENDPOINT}?${fetchService.requestParams(excelParams)}`;
};

export const PromptsComponent = ({ campaign, campaignConfig, reportStatus, reportDuration }: ILLMReportWidget) => {
    const {
        params: { topic: queryTopic, model, coreCompetitors },
    } = useCurrentStateAndParams();

    const {
        isOpen: isSidePanelOpen,
        selectedRow,
        openSidePanel,
        closeSidePanel,
    } = useSidePanelWrapper<Prompt>({ analyticsLabel: "Prompts Widget Side Panel" });

    const containerRef = useRef<HTMLDivElement>(null);
    const previousCampId = usePrevious<string>(campaign?.id);
    const areCoreCompetitorsSelected = coreCompetitors === "true";
    const coreCompetitorBrands = useMemo(() => {
        const competitors = campaign?.competitors ?? [];
        const allBrands = competitors.flatMap(({ brands = [] }) => brands);

        return [...new Set(allBrands)];
    }, [campaign?.competitors]);

    const { apiFilterParams: rawApiFilterParams, searchTerm } = usePromptsUrlParams();
    const apiFilterParams = useMemo<IPromptsApiFilterParams>(() => {
        if (!areCoreCompetitorsSelected || !coreCompetitorBrands?.length) {
            return rawApiFilterParams;
        }

        const urlBrands = rawApiFilterParams.brands ?? [];
        const mergedBrands = Array.from(new Set([...urlBrands, ...coreCompetitorBrands]));
        return { ...rawApiFilterParams, brands: mergedBrands };
    }, [rawApiFilterParams, areCoreCompetitorsSelected, coreCompetitorBrands]);

    const dispatch = useDispatch();
    const [currentPage, setCurrentPage] = useState(1);
    const [activeRow, setActiveRow] = useState<Prompt | undefined>();
    const [hasError, setHasError] = useState(false);
    const [promptsManagementData, setPromptsManagementData] = useState<IModifiableTopic[]>([]);
    const [sorting, setSorting] = useState<{ dataIndex: keyof Prompt; sortDirection: SortDirection }>({
        dataIndex: DEFAULT_SORT_COLUMN,
        sortDirection: "descend",
    });

    const { mutate: updateCampaign, isPending: isSavingCampaign } = useUpdateCampaign();

    const saveCampaign = (data: IModifiableTopic[]): void => {
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "edit prompts/save");
        updateCampaign(
            {
                campaignId: campaign.id,
                name: campaign.name,
                lastModifiedDate: campaign.lastModifiedDate,
                topics: data
                    .filter(({ topicId, prompts }) => topicId !== ALL_TOPICS_NAME && prompts.length)
                    .map(({ topicId, name, prompts }) => ({
                        topicId,
                        topicName: name,
                        prompts: prompts.map(({ promptId, query, intent }) => ({
                            promptId: !promptId.startsWith(NEW_PROMPT_ID_SUFFIX) ? promptId : null,
                            query,
                            intent,
                        })),
                    })),
            },
            {
                onSuccess: () => {
                    dispatch(showSuccessToast(translate("llm.prompts.edit.save.success")));
                },
                onError: (err) => {
                    allTrackers.dispatchTrackEvent("Edit Campaign", "error", `edit prompts/save`, {
                        campaignId: campaign?.id,
                        campaignName: campaign?.name,
                    });

                    dispatch(showErrorToast(translate("llm.prompts.edit.save.error")));
                    Sentry.errorTracker({
                        e: err,
                        message: "Error saving topic settings",
                        level: "error",
                        params: {
                            extra: {
                                moduleName: "llm-tracker",
                                campaignId: campaign?.id,
                                errorMessage: err,
                            },
                        },
                    });
                },
            },
        );
    };

    const {
        mutate: getCampaignDetails,
        isPending: isManagementDataLoading,
        error: managementDataError,
    } = useCampaignDetails();

    const {
        isOpen: isManagementModalOpen,
        openModal: openManagementModal,
        modal: editModal,
    } = usePromptsEditModal({
        data: promptsManagementData,
        onSave: saveCampaign,
        saveText: translate("global.save"),
        generationProps: {
            campaignId: campaign.id,
            domain: campaign?.primaryDomain?.url,
            cultureName: campaign.language.cultureName,
        },
        isLoading: isManagementDataLoading || isSavingCampaign,
        error: !!managementDataError,
    });

    const permissions = useAssetPermissions(campaign);
    const canManagePrompts = permissions?.isOwner ?? false;
    const canLoadData = reportStatus === ReportStatus.Ready && !!campaign;
    const globalTopics = getTopicsFilterParam(queryTopic);
    const baseApiParams = useMemo(
        () => ({
            ...getBaseWidgetRequestParams(campaign, campaignConfig, reportDuration),
            ...(globalTopics && { topics: globalTopics }),
        }),
        [campaign, campaignConfig, reportDuration, globalTopics],
    );

    const tableApiParams: PromptsRequestParams = useMemo(
        () =>
            ({
                ...baseApiParams,
                ...apiFilterParams,
                pageSize: FETCH_PAGE_SIZE,
                page: 1,
                returnLastDate: PROMPTS_TABLE_RETURN_LAST_DATE,
                sortField: SORT_FIELD_MAP[sorting.dataIndex],
                asc: sorting.sortDirection === "ascend",
            }) as PromptsRequestParams,
        [baseApiParams, apiFilterParams, sorting],
    );

    const { data: periodChangesData } = usePeriodChangesData(baseApiParams, { enabled: canLoadData });
    const hasPriorWindow = periodChangesData !== null && periodChangesData !== undefined;

    const {
        data,
        isLoading: isLoadingData,
        isFetching,
    } = usePrompts(tableApiParams, {
        enabled: canLoadData,
    });

    const { data: filtersData, isLoading: isCountsLoading } = useFiltersData(
        { ...baseApiParams, ...apiFilterParams },
        {
            enabled: canLoadData,
        },
    );

    const tableData = useMemo<Array<Prompt>>(() => data?.prompts ?? [], [data]);

    const brandsOptions = useMemo(() => {
        const uniqueBrands = new Set<string>();
        tableData.forEach((prompt) => {
            prompt.topBrands?.forEach((brand) => {
                if (brand.name) uniqueBrands.add(brand.name);
            });
        });
        return Array.from(uniqueBrands)
            .sort((a, b) => a.localeCompare(b))
            .map((name) => ({ id: name, value: name, label: name }));
    }, [tableData]);

    const domainsOptions = useMemo(() => {
        const uniqueDomains = new Set<string>();
        tableData.forEach((prompt) => {
            prompt.citationsWithInfluence?.forEach((citation) => {
                if (citation.link) {
                    const domain = extractDomain(citation.link);
                    if (domain) uniqueDomains.add(domain);
                }
            });
        });
        return Array.from(uniqueDomains)
            .sort((a, b) => a.localeCompare(b))
            .map((domain) => ({ id: domain, value: domain, label: domain }));
    }, [tableData]);

    const [totalCampaignPrompts, setTotalCampaignPrompts] = useState<number | undefined>();
    const excelDownloadUrl = useMemo(
        () => buildExcelUrl(baseApiParams, apiFilterParams),
        [baseApiParams, apiFilterParams],
    );

    const onPageChange = (current: number, _pageSize: number, nextPage: number): void => {
        const paginationDirection = nextPage > current ? "next" : "prev";
        allTrackers.dispatchTrackEvent("Pagination", "click", `Table/${paginationDirection}/${nextPage}`);
        setCurrentPage(nextPage);
    };

    const updateUrlParams = (updates: Record<string, string | undefined>) => {
        void proNavigator.updateParams(updates, { reload: false });
    };

    useEffect(() => {
        if (previousCampId && previousCampId !== campaign?.id) {
            navigateToReport(campaign, model, EAvailableTabs.PROMPTS);
        }
    }, [campaign, previousCampId, model]);

    useEffect(() => {
        setCurrentPage(1);
    }, [tableData]);

    useEffect(() => {
        if (data) {
            setTotalCampaignPrompts(data.totalCampaignPrompts);
        }
    }, [data]);

    useEffect(() => {
        if (data?.totalCount > FETCH_PAGE_SIZE) {
            const errorMessage = `Data integrity error: Total count (${data?.totalCount}) exceeds fetch limit (${FETCH_PAGE_SIZE}). Data may be incomplete.`;
            setHasError(true);

            Sentry.errorTracker({
                e: new Error(errorMessage),
                message: "Ai Tracker Prompts table data integrity error - there are more items than can be displayed",
                level: "error",
                params: {
                    extra: {
                        campaignId: campaign?.id,
                        totalCount: data.totalCount,
                        fetchLimit: FETCH_PAGE_SIZE,
                        actualDataLength: data.prompts?.length || 0,
                    },
                },
            });
        } else {
            setHasError(false);
        }
    }, [campaign, data]);

    const reportError = reportStatus === ReportStatus.Error;
    const isQuickFiltersLoading = (!canLoadData && !reportError) || isLoadingData || isCountsLoading;
    const shouldShowNoData = hasError || !data || reportError;
    const shouldShowLoading = (isLoadingData || isFetching || !campaign) && !reportError;
    const shouldShowCollectingDataBanner =
        reportStatus === ReportStatus.Ready && data?.hasPromptsPendingFirstCollection === true;
    const isManageButtonDisabled =
        !canManagePrompts || hasError || reportError || !canLoadData || !areAllModelsCompleted(campaign);
    const noDataProps = getWidgetNoDataProps(shouldShowNoData, hasError);

    useColumnPickerTracking("aibv.prompts.columns", "Prompt Tracking", containerRef);

    const columnsConfig = useMemo(
        () => getColumnConfig(sorting, areCoreCompetitorsSelected ? coreCompetitorBrands : undefined),
        [sorting, areCoreCompetitorsSelected, coreCompetitorBrands],
    );

    const handleSortBy = (dataIndex: keyof Prompt, sortDirection: SortDirection) => {
        setSorting({ dataIndex, sortDirection });
        allTrackers.dispatchTrackEvent("Sort", "Click", String(dataIndex));
    };

    const handleSearchChange = (term: string) => {
        updateUrlParams({ searchTerm: term || undefined });
    };

    const trackExcelDownload = () => allTrackers.dispatchTrackEvent("Download", "submit-ok", "Table/Excel");

    const excelExportConfig = {
        excelDownloadUrl,
        onExcelDownloaded: trackExcelDownload,
    };

    useEffect(() => {
        if (isManagementModalOpen) {
            getCampaignDetails(
                { campaignId: campaign?.id },
                {
                    onSuccess: (response) => {
                        setPromptsManagementData(
                            response?.topics?.map(({ name, prompts, ...props }) => ({
                                ...props,
                                name,
                                prompts: prompts?.map((prompt) => ({
                                    ...prompt,
                                    currentTopicName: name,
                                })),
                            })),
                        );
                    },
                    onError: (exception) => {
                        const errorObj = { campaignId: campaign?.id };
                        allTrackers.dispatchTrackEvent("Edit Campaign", "error", "edit prompts/open", errorObj);
                        Sentry.errorTracker({
                            message: "Failed to load campaign details",
                            e: exception,
                            level: "error",
                            params: {
                                extra: errorObj,
                            },
                        });
                    },
                },
            );
        }
    }, [isManagementModalOpen]);

    if (reportStatus === ReportStatus.NotReady) {
        return <ReportLoadingBanner />;
    }

    return (
        <>
            <PromptsQuickFilters
                hasCompetitors={campaign?.competitors?.length > 0}
                isLoading={Boolean(isQuickFiltersLoading && !filtersData)}
                counts={filtersData?.quickFilters}
            />
            <WidgetBoxWithMarginBottom
                ref={containerRef}
                data-automation="prompts-widget"
            >
                {shouldShowCollectingDataBanner ? (
                    <FullWidthMiniBanner
                        data-automation="prompts-collecting-data-banner"
                        backgroundColor={colorsPalettes.blue[100]}
                        bannerText={translate("llm.prompts.collecting-data.banner")}
                        iconColor={colorsPalettes.blue[400]}
                        iconName="alerts"
                    />
                ) : null}
                <TableWrapper>
                    <Table
                        id="prompts-table"
                        localStorageKey="aibv.prompts.columns"
                        size="large"
                        pagination={{
                            pageSize: PAGE_SIZE,
                            total: tableData?.length,
                            current: currentPage,
                            onChange: onPageChange,
                        }}
                        onSort={(sorterData) => {
                            handleSortBy(sorterData.columnKey as keyof Prompt, sorterData.order || "ascend");
                        }}
                        rowHoverable
                        sticky={false}
                        columnConfig={columnsConfig}
                        data={tableData}
                        theme="gray"
                        loaderRowCount={PAGE_SIZE}
                        isLoading={shouldShowLoading}
                        activeRowSelector={(row) => row.prompt === activeRow?.prompt}
                        onRow={(record) => ({
                            onClick: () => {
                                setActiveRow(record);
                                openSidePanel(record);
                            },
                            style: { cursor: "pointer" },
                        })}
                        noData={{
                            title: noDataProps.noDataTitle,
                            subtitle: noDataProps.noDataSubtitle,
                        }}
                    >
                        <Table.TableTop borderBottom>
                            <Table.TableTop.Title
                                title={translate("llm.prompts.title")}
                                tooltip={translate("llm.prompts.tooltip")}
                                extensions={
                                    canManagePrompts ? (
                                        <Flex
                                            alignItems="center"
                                            gap="20px"
                                        >
                                            {totalCampaignPrompts !== undefined ? (
                                                <PromptsCounter totalPrompts={totalCampaignPrompts} />
                                            ) : null}
                                            <Button
                                                type="outlined"
                                                isDisabled={isManageButtonDisabled}
                                                onClick={openManagementModal}
                                            >
                                                {translate("llm.prompts.edit.button")}
                                            </Button>
                                        </Flex>
                                    ) : null
                                }
                            />
                            <Table.TableTop.Settings
                                locale={{
                                    columnPicker: {
                                        mandatoryField: translate("common.columnsPicker.mandatoryField"),
                                    },
                                }}
                                columnPickerType="reorderable"
                                excelExportConfig={excelExportConfig}
                            />
                            <Table.TableTop.Filters
                                filters={[
                                    {
                                        filter: "search",
                                        placeholder: translate("llm.prompts.search.placeholder"),
                                        onSearchChange: handleSearchChange,
                                        initialValue: searchTerm,
                                    },
                                ]}
                                factoryFilters={
                                    <PromptsDataFilters
                                        brandsOptions={brandsOptions}
                                        domainsOptions={domainsOptions}
                                        coreCompetitorBrands={coreCompetitorBrands}
                                        isCoreCompetitorsSelected={areCoreCompetitorsSelected}
                                        isLoading={shouldShowLoading}
                                        hasPriorWindow={hasPriorWindow}
                                    />
                                }
                            />
                        </Table.TableTop>
                    </Table>
                </TableWrapper>
            </WidgetBoxWithMarginBottom>
            <PromptReportSidePanel
                isOpen={isSidePanelOpen}
                campaign={campaign}
                campaignConfig={campaignConfig}
                reportDuration={reportDuration}
                rowData={selectedRow}
                onClose={() => {
                    closeSidePanel();
                    setActiveRow(undefined);
                }}
            />
            {editModal}
        </>
    );
};
